import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { apiFetch } from "../../utils/apiFetch"
import { formatApiError } from "../../utils/formatApiError"

export default function PdfPreview({ file, documentId, provider = "euipo", fileName, endpoint, label }: {
  file?: File | null
  documentId?: string | null
  provider?: "euipo" | "patent"
  fileName?: string | null
  endpoint?: string
  label?: string
}) {
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState("")
  const [error, setError] = useState("")
  const closeButton = useRef<HTMLButtonElement>(null)
  const dialog = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    let active = true
    let objectUrl = ""
    setUrl("")
    setError("")
    const triggerElement = trigger.current
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    closeButton.current?.focus()
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false)
      if (event.key === "Tab") {
        const controls = dialog.current?.querySelectorAll<HTMLElement>("button, a[href], iframe")
        if (!controls?.length) return
        const first = controls[0]
        const last = controls[controls.length - 1]
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
      }
    }
    document.addEventListener("keydown", keydown)
    async function load() {
      try {
        const blob = file || await apiFetch(endpoint || `/api/documents/${provider}/${encodeURIComponent(documentId!)}/preview`, {}, "blob")
        if (!(blob instanceof Blob)) throw new Error("The PDF could not be loaded.")
        if (!active) return
        objectUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }))
        setUrl(objectUrl)
      } catch (err) {
        if (active) setError(formatApiError(err, "The PDF could not be loaded. Please try again."))
      }
    }
    load()
    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
      document.removeEventListener("keydown", keydown)
      document.body.style.overflow = previousOverflow
      triggerElement?.focus()
    }
  }, [open, file, documentId, provider, endpoint])

  const name = file?.name || fileName || "Document.pdf"
  if ((!file && !documentId && !endpoint) || (file && file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) || (!file && fileName && !fileName.toLowerCase().endsWith(".pdf"))) return null

  return <>
    <button ref={trigger} type="button" onClick={() => setOpen(true)} aria-label={`Preview ${name}`} title="Open PDF preview" className="inline-flex min-w-0 max-w-full items-center gap-2 text-sm font-medium text-blue-700 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"><svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 3H5a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h9M13 3l5 5v3M13 3v5h5M8 8h2M8 12h3M8 16h2" />
        <circle cx="16" cy="16" r="3.5" />
        <path d="m18.5 18.5 3 3" />
      </svg><span className="truncate">{label || name}</span></button>
    {open && createPortal(
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" onClick={() => setOpen(false)}>
        <div ref={dialog} role="dialog" aria-modal="true" aria-label={`PDF preview: ${name}`} className="flex h-[90vh] w-full max-w-6xl flex-col rounded-lg bg-white shadow-xl" onClick={event => event.stopPropagation()}>
          <div className="flex items-center justify-between gap-4 border-b p-4">
            <span className="truncate font-medium">{name}</span>
            <div className="flex shrink-0 gap-4">
              {url && <a href={url} target="_blank" rel="noreferrer" className="text-sm text-blue-700 hover:underline">Open in new tab</a>}
              <button ref={closeButton} type="button" onClick={() => setOpen(false)} className="text-sm font-medium text-gray-700 hover:underline">Close</button>
            </div>
          </div>
          {error ? <p role="alert" className="p-6 text-red-700">{error}</p> : url ? <iframe title={`PDF: ${name}`} src={url} className="min-h-0 w-full flex-1 border-0" /> : <p role="status" className="p-6">Loading PDF…</p>}
        </div>
      </div>, document.body
    )}
  </>
}
